/**
 * !Note[DND]: This file return JSX elements also that's why *tsx is being used an extension
 * TODO: [Table] Complete the competition button
 * TODO: [Types] Sync its types with other files and move to common-types.ts
 */

import React from "react";
import { Key } from "react";
import {
    CA_DD_MODULES,
    CatAnalyticsSubCatDrillDownContentViews,
    RevstackCASectionIds,
    RevstackDashboardSubModules,
    RevstackFrequencySlicerOptions,
    TableExportFormats
} from "@ts/common-types";
import { ColumnsType } from "antd/es/table";
import { renderWidgetStructure } from "./renderers";
import { DATE_PICKER_PRESET_PERIODS } from "@/ts/constants";

interface BaseActionPanelConfig {
    isSearchReq?: boolean;
    isCompetitionButtonReq?: boolean;
    isLayoutButtonReq?: boolean;
    isDownloadButtonReq?: boolean;
}

interface ExtendedActionPanelConfig extends BaseActionPanelConfig {
    searchHandler: SearchHandler;
    downloadButtonHandler: DownloadHandler;
    layoutButtonHandler: LayoutHandler;
    competitionButtonHandler: CompetitionHandler;
}

interface BaseColumnConfig {
    columnGrouping?: boolean;
    columnKeys?: string[];
}

interface BaseWidgetConfig extends BaseColumnConfig {
    tableHeight?: number;
    actionPanelConfig?: BaseActionPanelConfig;
}

type SearchHandler = (e: any) => void;
type DownloadHandler = (format?: TableExportFormats) => void;
type LayoutHandler = (widgetKey: string) => any;
type CompetitionHandler = () => void;

interface BaseTableColumn {
    title: string;
    dataIndex: string;
    key: string;
    width?: number;
    align?: 'left' | 'right' | 'center';
    className?: string;
}

interface SortableTableColumn extends BaseTableColumn {
    sorter: (a: any, b: any) => number;
    showSorterTooltip?: { target: string };
}

interface FilterableTableColumn extends BaseTableColumn {
    filters: any[];
    onFilter: (value: boolean | Key, record: any) => boolean;
}

interface PinnableTableColumn extends BaseTableColumn {
    fixed?: 'left' | 'right';
}

interface RenderableTableColumn extends BaseTableColumn {
    render: (text: any, record?: any, index?: number) => React.ReactNode;
}

interface GroupedColumn extends BaseTableColumn {
    children: TableColumn[];
    order?: number;
}

type TableColumn = BaseTableColumn | SortableTableColumn | FilterableTableColumn | PinnableTableColumn | RenderableTableColumn | GroupedColumn;

interface ColumnVisibilityState {
    [key: string]: boolean;
}

interface GenericWidget<TConfig = any, TData = any> {
    identifier: string;
    widgetComponent: React.ComponentType<any>;
    config?: TConfig;
    formatter: (widgetConfig: any, widgetData: any) => TData;
}

interface GenericConfig<T extends BaseWidgetConfig = BaseWidgetConfig> {
    sectionId: string;
    title: string;
    config?: T;
}

interface GenericDrilldownConfig<T extends BaseWidgetConfig = BaseWidgetConfig> extends GenericConfig<T> {
    tabKey: CA_DD_MODULES;
    widgets: GenericWidget<T>[][];
}

interface FilterState {
    [key: string]: {
        filters?: {
            search?: string;
            [key: string]: any;
        };
        [key: string]: any;
    };
}

type ColumnVisibilityMap = Record<CA_DD_MODULES, ColumnVisibilityState>;
type LayoutDropdownMap = Record<string, boolean>;
type ColDefsMap = Record<CA_DD_MODULES, ColumnsType<any>>;
type SectionConfigMap = Record<RevstackCASectionIds, SectionConfig>;

type FilterDispatchAction = (action: any) => void;

interface DrilldownConfigBase extends BaseColumnConfig {
    groupOrder?: string[];
}

interface IGetRevstackDDTableConfig {
    isAllSelectedInBaseFilter: boolean;
    drilldownConfig: DrilldownConfig[];
    activeTab: RevstackDashboardSubModules | CA_DD_MODULES;
    filterState: FilterState;
    filterDispatch: FilterDispatchAction;
    widgetData: any;
    sectionId: RevstackCASectionIds[];
}

interface IGetRevstackDDDefaultWidgetConfig {
    drilldownConfig: any[];
    widgetData: any;
    sectionIds: RevstackCASectionIds[];
    type: string;
    selectedTimePeriod?: DATE_PICKER_PRESET_PERIODS;
    granularity?: RevstackFrequencySlicerOptions;
    activeTab?: RevstackDashboardSubModules | CA_DD_MODULES;
    handlePeriodAndGranularityChange?: (timePeriod: DATE_PICKER_PRESET_PERIODS, granularity: RevstackFrequencySlicerOptions) => void;
    pillFilterDropDownData?: Record<string, any[]>;
    setPillFilterDropDownData?: (cache: Record<string, any[]> | ((prev: Record<string, any[]>) => Record<string, any[]>)) => void;
}

type DrilldownConfig = GenericDrilldownConfig<DrilldownConfigBase>;

interface WidgetConfigData {
    identifier: string;
    widgetComponent: React.ComponentType<any>;
    config: {
        colDefs: any[];
        tableHeight?: number;
        actionPanelConfig: ExtendedActionPanelConfig;
    };
    data: any;
    title: string;
}

interface SectionConfig {
    value: CatAnalyticsSubCatDrillDownContentViews;
    component: React.ReactNode;
    widgets: {
        sectionId: string;
        index: number;
        title: string;
        widgets: WidgetConfigData[][];
    }[];
}

export const getRevstackDDTableConfig = ({
    isAllSelectedInBaseFilter,
    drilldownConfig,
    activeTab,
    filterState,
    filterDispatch,
    widgetData,
    sectionId,
}: IGetRevstackDDTableConfig): SectionConfigMap => {

    const filteredDrilldownConfig = isAllSelectedInBaseFilter
        ? drilldownConfig
        : drilldownConfig.filter(
              (widgetConfig: any) =>
                  widgetConfig.tabKey !== CA_DD_MODULES.OVERVIEW
          );

    const config = sectionId.reduce((acc, currentSectionId) => {
        acc[currentSectionId] = {
            value: CatAnalyticsSubCatDrillDownContentViews.TABLE_VIEW,
            component: filteredDrilldownConfig
            .filter(
                (widgetConfig: any) =>
                    widgetConfig.sectionId === currentSectionId
            )
            .map((widgetConfig: any, idx: number) => {
                const ParentWidgetComponent = widgetConfig.widget;
                return <ParentWidgetComponent
                widgetConfig={widgetConfig}
                widgetData={widgetData}
                title=""
                modalConfig={{
                    onModalOpen: () => {},
                }}
                configSessionKey={`revstack-${currentSectionId}`}
                />
            }),
            widgets: [],
        };
        return acc;
    }, {} as SectionConfigMap);

    return config;
};

export const getRevstackDDDefaultWidgetConfig = ({
    drilldownConfig,
    widgetData,
    sectionIds,
    type,
    selectedTimePeriod,
    granularity,
    activeTab,
    handlePeriodAndGranularityChange,
    pillFilterDropDownData,
    setPillFilterDropDownData,
}: IGetRevstackDDDefaultWidgetConfig) => {
    
    const result: Record<RevstackCASectionIds, any> = {} as Record<RevstackCASectionIds, any>;
    
    sectionIds.forEach(sectionId => {
        result[sectionId] = {
            component: drilldownConfig
                .filter(
                    (widgetConfig: any) => widgetConfig.sectionId === sectionId
                )
                .map((widgetConfig: any, idx: number) => {
                    //! Note: This is trends widget without parent component (trying to eliminate top level parent component)
                    if(type === 'trends'){
                        return renderWidgetStructure(
                            widgetConfig.widgets,
                            (widget: any, widgetIndex: number, keyPrefix?: string) => {
                                const ChildWidgetComponent = widget.widgetComponent;
                                const formattedWidgetData = widget.formatter(widget, widgetData);
                                return (
                                    <div key={keyPrefix || `${widgetConfig.sectionId}-${idx}-${widgetIndex}`}>
                                        <ChildWidgetComponent
                                            widgetConfig={widgetConfig}
                                            widget={widget}
                                            widgetData={formattedWidgetData}
                                            isLoading={formattedWidgetData?.isLoading}
                                            title={widgetConfig.title}
                                            config={widgetConfig.config}
                                            graphAttributes={widgetConfig.config?.graphAttributes}
                                            primaryDimension={widget.queryMetaInfo?.primaryDimension}
                                            identifier={widget.identifier}
                                            tabKey={widgetConfig.tabKey}
                                        />
                                    </div>
                                );
                            },
                            `${widgetConfig.sectionId}-${idx}`
                        );
                    }
                    else{
                        const WidgetComponent = widgetConfig.widget;
                        return (
                            <div key={`${widgetConfig.sectionId}-${idx}`}>
                                <WidgetComponent
                                    widgetConfig={widgetConfig}
                                    widgetData={widgetData}
                                    title={widgetConfig.title}
                                    config={widgetConfig.config}
                                    activeTab={activeTab}
                                    selectedTimePeriod={selectedTimePeriod}
                                    granularity={granularity}
                                    handlePeriodAndGranularityChange={handlePeriodAndGranularityChange}
                                    pillFilterDropDownData={pillFilterDropDownData}
                                    setPillFilterDropDownData={setPillFilterDropDownData}
                                />
                            </div>
                        )
                    }
                }),
        };
    });
    
    return result;
}